import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import axiosInstance from '../../utils/axios';
import './LiveChatPage.css';

function LiveChatPage() {
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(true);
    const clientRef = useRef(null);
    const messagesEndRef = useRef(null);

    // Fetch previous messages when component mounts
    useEffect(() => {
        const token = localStorage.getItem('authToken');
        const username = localStorage.getItem('username');

        if (!token || !username) {
            navigate('/login');
            return;
        }

        // Fetch previous messages from the database
        const fetchPreviousMessages = async () => {
            try {
                setIsLoadingMessages(true);
                const response = await axiosInstance.get('/api/chat/messages');
                if (response.data && Array.isArray(response.data)) {
                    setMessages(response.data);
                }
            } catch (error) {
                console.error('Error fetching previous messages:', error);
            } finally {
                setIsLoadingMessages(false);
            }
        };

        fetchPreviousMessages();
    }, [navigate]);

    // Initialize WebSocket connection
    useEffect(() => {
        const token = localStorage.getItem('authToken');
        const username = localStorage.getItem('username');

        if (!token || !username) {
            return;
        }

        // Initialize STOMP client
        // Use protocol-relative URL: in production, use current protocol (HTTPS), in dev use http://localhost:8080
        const wsUrl = process.env.NODE_ENV === 'production' 
            ? `${window.location.protocol === 'https:' ? 'https' : 'http'}://${window.location.host}/ws`
            : 'http://localhost:8080/ws';
        
        const client = new Client({
            webSocketFactory: () => new SockJS(wsUrl),
            connectHeaders: {
                Authorization: `Bearer ${token}`
            },
            onConnect: () => {
                setIsConnected(true);
                // Subscribe to messages
                client.subscribe('/topic/messages', (message) => {
                    const chatMessage = JSON.parse(message.body);
                    setMessages((prev) => {
                        // Check if message already exists (prevent duplicates)
                        const exists = prev.some(m => 
                            (m.id && chatMessage.id && m.id === chatMessage.id) ||
                            (m.content === chatMessage.content && 
                             m.sender === chatMessage.sender && 
                             m.timestamp === chatMessage.timestamp)
                        );
                        if (exists) {
                            return prev;
                        }
                        return [...prev, chatMessage];
                    });
                });
            },
            onDisconnect: () => {
                setIsConnected(false);
            },
            onStompViga: (frame) => {
                console.error('STOMP error:', frame);
                setIsConnected(false);
            },
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
        });

        clientRef.current = client;
        client.activate();

        return () => {
            if (clientRef.current) {
                clientRef.current.deactivate();
            }
        };
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = () => {
        if (!inputMessage.trim() || !isConnected || !clientRef.current) {
            return;
        }

        const message = {
            content: inputMessage.trim(),
            sender: localStorage.getItem('username')
        };

        clientRef.current.publish({
            destination: '/app/chat.send',
            body: JSON.stringify(message)
        });

        setInputMessage('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className="live-chat-page">
            <div className="chat-container">
                <div className="chat-header">
                    <h2>Vestlus</h2>
                    <div className={`connection-status ${isConnected ? 'connected' : 'ühendus puudub'}`}>
                        {isConnected ? 'ühendatud' : 'Ühendus puudub'}
                    </div>
                </div>
                <div className="messages-container">
                    {isLoadingMessages && <div>Sõnumite laadimine...</div>}
                    {messages.map((msg) => (
                        <div key={msg.id || `${msg.sender}-${msg.timestamp}-${msg.content}`} className={`message ${msg.sender === localStorage.getItem('username') ? 'own-message' : ''}`}>
                            <div className="message-sender">{msg.sender}</div>
                            <div className="message-content">{msg.content}</div>
                            <div className="message-time">
                                {msg.timestamp ? (() => {
                                    try {
                                        const date = new Date(msg.timestamp);
                                        return isNaN(date.getTime()) ? '' : date.toLocaleTimeString();
                                    } catch {
                                        return '';
                                    }
                                })() : ''}
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>
                <div className="chat-input-container">
                    <input
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={isConnected ? "Alusta kirjutamist..." : "Ühendamine..."}
                        disabled={!isConnected}
                        className="chat-input"
                    />
                    <button
                        onClick={sendMessage}
                        disabled={!isConnected || !inputMessage.trim()}
                        className="send-button"
                    >
                        Saada
                    </button>
                </div>
            </div>
        </div>
    );
}

export default LiveChatPage;
